import { useMemo } from 'react';

import {
  usePublishedDocument,
} from '../guide/usePublishedGuides';
import type { PublishedGuideListing } from '../guide/publishedGuides';
import { useHiddenHolidays } from '../holidayPrefs/useHiddenHolidays';
import {
  HUB_DOC_ID,
  HUB_FINDS_COLLECTION,
  HUB_OCCASIONS_COLLECTION,
  HUB_TRENDING_COLLECTION,
  parseHubFinds,
  parseHubOccasions,
  parseHubTrending,
  resolveHubOccasionTiles,
  type HubFinds,
  type HubOccasion,
  type HubOccasionTile,
  type HubPick,
} from './hubDocs';
import { hubOccasionRows, type HubOccasionRowTile } from './hubOccasionSeasonal';
import type { MoreGuidePolicy } from './hubMoreGuides';

/**
 * Readers for the gift-guide hub's published documents (THE-582, THE-817).
 *
 * All go through `usePublishedDocument` — the same live-subscribe + strict-parse
 * path `usePublishedGuideIndex` uses — and then collapse its four states into
 * ONE thing the layout can render unconditionally: an array. Loading, absent,
 * permission-denied and malformed all come back as `[]`.
 *
 * That collapse is the contract the hub layout is allowed to depend on: an
 * unread or unwritten doc costs an empty row, never a crash and never a broken
 * tile. It is also why these readers can land before the layout and before the
 * board sends real data — there is no gate of their own to build.
 *
 * The collapse used to also mean the hub could not tell "still loading" from
 * "nothing to show". The two BAND readers now report `loading` alongside the
 * collapsed value (THE-1037): their data is only the first of two trips — the
 * product fan-out that hydrates it is the second — and a first-screen gate
 * that cannot see the gap between them reveals the page with two empty rows
 * in it and then drops the content in, which is the layout shift this field
 * exists to close. The collapse itself is unchanged: `loading` says nothing
 * about WHAT a settled read holds, only whether it has reported yet, so an
 * absent or denied document is still an empty row and never a crash.
 *
 * The occasion readers keep the plain collapse. They are one trip, and their
 * tiles are already part of the page the gate waits for.
 */

export interface HubTrendingPicksState {
  /**
   * Identity-stable while the doc is unchanged, so a consumer can use it as an
   * effect dependency (product hydration does) without refetching per render.
   */
  picks: HubPick[];
  /**
   * True until the document read has REPORTED — ready, missing, denied or
   * malformed. `skip` means no read was started, so it is never loading.
   *
   * This is the first leg of the band's two-trip load; `useHubPicksBand` ORs
   * it with the product hydration's own in-flight flag to give the hub's
   * first-screen gate one honest "this row is not finished yet" (THE-1037).
   */
  loading: boolean;
}

/**
 * The "Our picks this week" row, in render order, WITH guide attribution.
 *
 * Returns exactly what the doc holds and nothing else — no re-ranking, no
 * filtering, no knowledge of whether a human or a nightly rollup ordered it.
 * That is what makes replacing the curated list with a computed one a Firestore
 * write with no frontend change (THE-577 plan §3).
 *
 * `pick.guideSlug` is absent on every pick while the live doc still holds the
 * THE-582 `productIds` shape; see `parseHubTrending`. A caller rendering chips
 * must therefore treat a missing slug as "no chip", not as an error.
 */
export function useHubTrendingPicks(skip = false): HubTrendingPicksState {
  const { data, status } = usePublishedDocument(
    HUB_TRENDING_COLLECTION,
    HUB_DOC_ID,
    parseHubTrending,
    skip,
  );
  return useMemo(
    () => ({ picks: data ?? EMPTY_PICKS, loading: !skip && status === 'loading' }),
    [data, status, skip],
  );
}

/**
 * Just the IDs of the row above, for hydration.
 *
 * Kept as its own hook because product hydration takes IDs and uses the array
 * as an effect dependency — so it needs one identity per document, which a
 * `.map()` at the call site would not give it.
 */
export function useHubTrendingProductIds(skip = false): string[] {
  const { picks } = useHubTrendingPicks(skip);
  return useMemo(
    () => (picks.length ? picks.map((pick) => pick.productId) : EMPTY_IDS),
    [picks],
  );
}

export interface HubFindsState {
  /** The document, or `undefined` for loading/absent/denied/malformed. */
  finds?: HubFinds;
  /** As `HubTrendingPicksState.loading` — the first leg of two (THE-1037). */
  loading: boolean;
}

/**
 * `theaWebHubFinds/current` — the "Finds under $25" band (THE-817).
 *
 * Collapses to `undefined` rather than to an empty object, because this band
 * has a `weekOf` and there is no honest value to invent for it. Everything
 * else in this module collapses to `[]` for the same reason it can: an empty
 * array IS a renderable band. `{weekOf: ''}` would not be.
 *
 * The band is hidden when this is `undefined`, which covers loading, absent,
 * denied and malformed exactly as the other readers do — and "absent" is the
 * normal state until the board writes the first week.
 */
export function useHubFinds(skip = false): HubFindsState {
  const { data, status } = usePublishedDocument(
    HUB_FINDS_COLLECTION,
    HUB_DOC_ID,
    parseHubFinds,
    skip,
  );
  return useMemo(
    () => ({ finds: data ?? undefined, loading: !skip && status === 'loading' }),
    [data, status, skip],
  );
}

/** The raw occasion mapping. Prefer `useHubOccasionTiles` — see its doc. */
export function useHubOccasionMapping(skip = false) {
  return usePublishedDocument(
    HUB_OCCASIONS_COLLECTION,
    HUB_DOC_ID,
    parseHubOccasions,
    skip,
  );
}

export function useHubOccasions(skip = false): HubOccasion[] {
  const { data } = useHubOccasionMapping(skip);
  return data ?? EMPTY_OCCASIONS;
}

/**
 * The occasion tiles, already resolved against the published guide index and
 * with dead/off-season entries dropped.
 *
 * This — not `useHubOccasions` — is what a layout should call. The mapping is
 * hand-authored, so resolution is not an optimization, it is the check that
 * makes a tile pointing at nothing impossible; a caller that reads the raw
 * mapping and renders `guideSlugs` itself would reintroduce exactly the stranded
 * tile the design eliminates.
 *
 * `listings` is the caller's existing `usePublishedGuideIndex()` data — passed
 * in rather than read again here so the hub spends one index read, not two, and
 * so the tiles can never disagree with the grid rendered from the same array.
 */
export function useHubOccasionTiles(
  listings: PublishedGuideListing[] | undefined,
  now: Date,
  skip = false,
): HubOccasionTile[] {
  const occasions = useHubOccasions(skip);
  // `now` is a fresh Date on most renders, so memoize on its day — the season
  // window has day granularity and nothing below it can change the outcome.
  const day = `${now.getMonth() + 1}-${now.getDate()}`;
  return useMemo(
    () => (listings?.length ? resolveHubOccasionTiles(occasions, listings, now) : EMPTY_TILES),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [occasions, listings, day],
  );
}

/**
 * `useHubOccasionTiles`'s tiles, split into the seasonal and everyday rows and
 * put in display order (THE-819) — seasonal first, soonest first, then
 * everyday in the mapping's own order. See `hubOccasionRows` for the rule.
 *
 * `useHiddenHolidays` (not `useHolidayPrefs`) on purpose, same reasoning as
 * every other render surface that reads the saved list: this hook is called
 * from the hub's render path, and `useHolidayPrefs` drags the Functions SDK
 * into that path's module graph for a write capability the hub never uses.
 */
export function useHubOccasionRows(
  listings: PublishedGuideListing[] | undefined,
  now: Date,
  skip = false,
): HubOccasionRowTile[] {
  return useHubOccasionBrowse(listings, now, skip).occasionTiles;
}

/** Legacy fallback shares one mapping and preference read between both rows. */
export function useHubOccasionBrowse(listings: PublishedGuideListing[] | undefined, now: Date, skip = false): {
  occasionTiles: HubOccasionRowTile[]; moreGuidePolicy: MoreGuidePolicy;
} {
  const { data, status } = useHubOccasionMapping(skip);
  const { hiddenHolidays, ready, error } = useHiddenHolidays();
  const occasions = data ?? EMPTY_OCCASIONS;
  const day = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
  return useMemo(() => {
    const holidayPrefs = { hiddenHolidays, ready };
    const tiles = listings?.length ? resolveHubOccasionTiles(occasions, listings, now) : EMPTY_TILES;
    return {
      occasionTiles: hubOccasionRows(tiles, now, holidayPrefs),
      moreGuidePolicy: { occasions, holidayPrefs, loading: (!skip && status === 'loading') || (!ready && !error) },
    };
    // Calendar resolution is day-granular, as in useHubOccasionTiles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [occasions, listings, day, hiddenHolidays, ready, error, status, skip]);
}

const EMPTY_IDS: string[] = [];
const EMPTY_PICKS: HubPick[] = [];
const EMPTY_OCCASIONS: HubOccasion[] = [];
const EMPTY_TILES: HubOccasionTile[] = [];
